import classNames from 'classnames'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { NavigationIcon } from '@icons/NavigationIcon'

import styles from './Scrollbar.module.scss'

interface ScrollbarProps {
  parentRef: React.RefObject<HTMLDivElement | null>
}

export const Scrollbar: FC<ScrollbarProps> = ({ parentRef }) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(0)
  const [thumbLeft, setThumbLeft] = useState(0)
  const [isScrollable, setIsScrollable] = useState(false)
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartThumbLeft, setDragStartThumbLeft] = useState(0)

  const updateScrollbar = useCallback(() => {
    const parent = parentRef.current
    if (!parent) {
      return
    }
    const { scrollLeft, scrollWidth, clientWidth } = parent
    // added offset for very small differences in width
    const SCROLL_TOLERANCE = 30
    const isScrollable = scrollWidth > clientWidth
    const isScrollbarVisible = scrollWidth > clientWidth + SCROLL_TOLERANCE

    setIsScrollable(isScrollable)
    setIsScrollbarVisible(isScrollbarVisible)

    if (isScrollable) {
      parent.classList.add('custom-scrollbar-active')
    } else {
      parent.classList.remove('custom-scrollbar-active')
    }

    if (isScrollable) {
      const arrowIconSize = 24
      const trackMargin = 8
      const trackWidth =
        trackRef.current?.clientWidth || clientWidth - (arrowIconSize * 2 + trackMargin)

      const newThumbWidth = (clientWidth / scrollWidth) * trackWidth
      const maxScrollLeft = scrollWidth - clientWidth
      const maxThumbLeft = trackWidth - newThumbWidth
      const newThumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0
      setThumbWidth(newThumbWidth)
      setThumbLeft(newThumbLeft)
    }
  }, [parentRef])

  useEffect(() => {
    // small delay to ensure the DOM is fully rendered
    const timer = setTimeout(() => {
      updateScrollbar()
    }, 100)

    const parent = parentRef.current
    if (!parent) {
      return () => clearTimeout(timer)
    }

    const handleScroll = () => updateScrollbar()
    const handleResize = () => {
      setIsScrollable(false)
      updateScrollbar()
    }

    parent.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timer)
      parent.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [parentRef, updateScrollbar])

  useEffect(() => {
    if (!isDragging) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const parent = parentRef.current
      if (!parent) {
        return
      }
      const trackWidth = trackRef.current?.clientWidth || parent.clientWidth
      const { scrollWidth, clientWidth } = parent
      const maxScrollLeft = scrollWidth - clientWidth
      const maxThumbLeft = trackWidth - thumbWidth
      const delta = e.clientX - dragStartX
      const newThumbLeft = Math.min(Math.max(dragStartThumbLeft + delta, 0), maxThumbLeft)

      setThumbLeft(newThumbLeft)

      const scrollRatio = maxThumbLeft > 0 ? newThumbLeft / maxThumbLeft : 0
      parent.scrollLeft = scrollRatio * maxScrollLeft
    }

    const handleMouseUp = () => setIsDragging(false)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStartX, dragStartThumbLeft, thumbWidth, parentRef])

  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
    setDragStartX(e.clientX)
    setDragStartThumbLeft(thumbLeft)
  }

  if (!isScrollable) {
    return null
  }

  return (
    <div
      className={classNames(styles.scrollbar, {
        [styles.scrollbarHidden]: !isScrollbarVisible,
      })}
    >
      <div className={styles.leftArrow}>
        <NavigationIcon
          size={24}
          onClick={() => parentRef.current?.scrollTo({ left: 0, behavior: 'smooth' })}
          direction='left'
        />
      </div>
      <div className={styles.scrollbarTrack} ref={trackRef}>
        <div
          className={styles.thumb}
          onMouseDown={handleThumbMouseDown}
          style={{
            width: thumbWidth,
            insetInlineStart: thumbLeft,
            position: 'absolute',
            top: 0,
            bottom: 0,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        />
      </div>
      <div className={styles.rightArrow}>
        <NavigationIcon
          size={24}
          onClick={() =>
            parentRef.current?.scrollTo({
              left: parentRef.current!.scrollWidth,
              behavior: 'smooth',
            })
          }
          direction='right'
        />
      </div>
    </div>
  )
}
